import React, { useState } from "react";
import Icon from "../AppIcon";

const HELP_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "Rocket",
    articles: [
      {
        title: "What is TRONC?",
    content:
    "TRONC is a system for pooling and distributing tips, gratuities, and service charges to staff. Under UK law, a TRONC scheme must be managed by an independent 'Troncmaster' and tips distributed through the payroll system to ensure correct tax treatment.",
      },
      {
        title: "Setting Up Your Account",
     content:
          "1. Complete your company details in Settings → Company.\n2. Connect your HR and Till integrations.\n3. Run a full sync to import locations, employees, and job roles.\n4. Configure distribution rules for each location.\n5. Start recording daily TRONC takings.",
   },
      {
        title: "Understanding Roles",
        content:
          "• Global Admin – Full access to all tenants and system settings.\n• Tenant Admin – Full access within your organisation.\n• Manager – Can view data and run distributions for assigned locations.\n• User – View-only access to their own TRONC data.",
      },
    ],
  },
  {
id: "daily-takings",
    title: "Daily Takings",
    icon: "PoundSterling",
    articles: [
      {
        title: "Recording Daily TRONC",
        content:
          "Navigate to Daily Takings & TRONC to record each day's tip pool. If you have a Till integration, data is synced automatically. You can also enter amounts manually for each location.",
      },
 {
        title: "Cash Tips vs Card Tips",
        content:
   "The system tracks both cash and card tips separately. Card tips are pulled from your Till integration. Cash tips can be entered manually. Both are combined into the total TRONC pot for distribution.",
      },
    ],
  },
  {
    id: "distributions",
    title: "Distributions",
    icon: "PieChart",
    articles: [
      {
  title: "How Distribution Works",
    content:
          "1. Select a TRONC record (a day's takings).\n2. The system calculates each eligible employee's share based on your rules.\n3. Review the breakdown by role and employee.\n4. Approve the distribution — this locks the record.\n5. Approved distributions are exported for payroll processing.",
      },
      {
      title: "Distribution Methods",
      content:
          "• Points-Based (by Role) – Each job role has a weighting. Employees earn points based on role × hours worked.\n• Equal Split – Divides the pot equally among all eligible employees.\n• Custom – Manually set each employee's share.\n• By Department – Allocates based on department groupings.",
      },
      {
        title: "Eligibility Rules",
    content:
          "Employees must meet certain criteria to receive a share:\n• Must be marked as TRONC-eligible.\n• Must have clocked in during the period.\n• May need to meet minimum hours thresholds.\n• Probationary employees may receive a reduced share based on seniority bands.",
   },
],
  },
  {
    id: "compliance",
    title: "HMRC Compliance",
    icon: "Shield",
    articles: [
      {
        title: "HMRC Requirements",
        content:
          "HMRC requires that TRONC distributions are:\n• Managed by an independent Troncmaster.\n• Processed through payroll for tax/NI purposes.\n• Recorded with a full audit trail.\n• Distributed fairly without employer interference.\n\nRocket Tronc helps you meet all these requirements.",
      },
      {
        title: "Audit Trail",
        content:
          "Every action in the system is logged — who made changes, when, and what was modified. This includes distribution approvals, rule changes, manual overrides, and data imports. Access the full audit trail from the Audit Trail page.",
      },
    ],
  },
  {
    id: "integrations",
    title: "Integrations",
    icon: "Link",
    articles: [
      {
        title: "Connecting Your HR System",
        content:
     "Go to Settings → Company or System → Integrations. Select your HR provider and enter your API credentials. Rocket integrations (RocketHR) require no credentials — just enable them.\n\nOnce connected, use the sync buttons to import locations, job roles, and employees.",
      },
      {
        title: "Connecting Your Till/POS",
        content:
          "Similar to HR, connect your Till provider to automatically import daily TRONC and service charge data. Synced data appears in Daily Takings & TRONC.",
      },
      {
  title: "Running Syncs",
        content:
          "• Quick Sync – Syncs clock records and TRONC data for the last 7 days.\n• Full HR Sync – Syncs locations → job roles → employees in order.\n• Individual Syncs – Sync specific data types separately.\n\nAutomatic syncs run daily at the configured time.",
      },
    ],
  },
];

const HelpPanel = ({ isOpen, onClose }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  if (!isOpen) return null;

  const filteredSections = searchQuery
    ? HELP_SECTIONS.map((section) => ({
     ...section,
        articles: section.articles.filter(
          (a) =>
            a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.content.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      })).filter((s) => s.articles.length > 0)
    : HELP_SECTIONS;

  return (
    <>
      {/* Backdrop */}
 <div
        className="fixed inset-0 z-[4000] bg-black/30"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-[4001] w-full max-w-md bg-card border-l border-border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
    <div className="flex items-center gap-2">
    <Icon name="HelpCircle" size={20} className="text-primary" />
            <h2 className="text-lg font-heading font-semibold">
  Help & Support
  </h2>
          </div>
          <button
       onClick={onClose}
      className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
   </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border shrink-0">
          <div className="relative">
            <Icon
  name="Search"
  size={16}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
     />
    <input
  type="text"
    value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search help articles..."
    className="input w-full pl-9"
      />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredSections.length === 0 ? (
 <div className="text-center py-8">
       <Icon
     name="SearchX"
         size={32}
             className="text-muted-foreground mx-auto mb-3"
          />
      <p className="text-muted-foreground">
                No articles found for "{searchQuery}"
           </p>
            </div>
          ) : (
     <div className="space-y-2">
  {filteredSections.map((section) => (
        <div
          key={section.id}
       className="border border-border rounded-lg overflow-hidden"
      >
    {/* Section header */}
      <button
 onClick={() =>
             setExpandedSection(
              expandedSection === section.id ? null : section.id,
         )
  }
          className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
     >
    <div className="flex items-center gap-2">
     <Icon
     name={section.icon}
              size={18}
   className="text-primary"
        />
          <span className="font-semibold text-sm">
               {section.title}
         </span>
  <span className="text-xs text-muted-foreground">
       ({section.articles.length})
      </span>
        </div>
          <Icon
    name={
 expandedSection === section.id
      ? "ChevronUp"
  : "ChevronDown"
    }
         size={16}
   className="text-muted-foreground"
           />
      </button>

       {/* Articles */}
         {expandedSection === section.id && (
                <div className="border-t border-border">
            {section.articles.map((article, i) => (
            <div key={i} className="border-b border-border last:border-b-0">
   <button
         onClick={() =>
            setExpandedArticle(
   expandedArticle === `${section.id}-${i}`
   ? null
       : `${section.id}-${i}`,
     )
  }
   className="w-full flex items-center justify-between p-3 pl-10 hover:bg-muted/30 transition-colors"
        >
  <span className="text-sm font-medium text-left">
       {article.title}
    </span>
     <Icon
           name={
      expandedArticle === `${section.id}-${i}`
   ? "ChevronUp"
             : "ChevronDown"
     }
     size={14}
        className="text-muted-foreground shrink-0 ml-2"
         />
        </button>

   {expandedArticle === `${section.id}-${i}` && (
   <div className="px-10 pb-3">
  <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
     {article.content}
             </div>
            </div>
)}
         </div>
       ))}
</div>
 )}
             </div>
              ))}
            </div>
          )}
        </div>

{/* Footer */}
    <div className="p-4 border-t border-border shrink-0">
   <p className="text-xs text-muted-foreground text-center">
            Need more help?{" "}
         <a
  href="mailto:support@rockettronc.com"
       className="text-primary hover:underline"
          >
  Contact support
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default HelpPanel;
